import { Injectable } from '@angular/core';
import { IProductInfo } from '@core/product/models/product-info.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { IProductSession } from '@core/product/models/product-session.model';
import { IRestrictedAddons } from '@core/product/models/restricted-addons.model';
import { DateFormatService } from '@core/date/date-format.service';
import { ProductAvailableHelperService } from '@core/product/product-available-helper.service';
import { CSessionBasedProductTypes, TProductType } from '@core/product/models/product-type.type';
import { ProductCapacityService } from '@roller/product-capacity-service';
import { ICXCapacityProduct, ICXCapacitySession } from '@core/product/models/product-capacity.model';
import { rdlIsNumber } from '@roller/rdl/utilities/number';
import { CMaxOrderItems } from '@core/availability-calculation/availability-calculation.const';
import { ProductHelperService } from './product-helper.service';
import { IProduct } from './models/product.model';

/**
 * Takes a ticket item (product: IProductInfo) and returns the remaining value that can be added to the ticket item.
 * Example ticket with a current max booking limit of 15 has combined orderItem quantity of 13 has a result of 2.
 */
@Injectable({
  providedIn: 'root'
})
export class ProductAvailableService {
  private readonly maxOrderItems = 999;
  private readonly typesWithoutRequiredAvailability: TProductType[] = ['giftcard', 'wallet', 'addon'];

  constructor(
    private productAvailableHelperService: ProductAvailableHelperService,
    private dateFormatService: DateFormatService,
    private productCapacityService: ProductCapacityService,
    private productHelperService: ProductHelperService
  ) {}

  getMaxAddAmount(
    product: IProductInfo,
    orderItems: IOrderItem[],
    bookingDate: Date,
    session?: IProductSession,
    ignoreOnlineSalesClosed?: boolean,
    restrictedAddOns?: IRestrictedAddons[]
  ): number {
    if (!this.isProductAvailable(product.id, bookingDate, session)) {
      return 0;
    }

    if (!this.canAddRestrictedAddon(product, session, restrictedAddOns)) {
      return 0;
    }

    if (!this.canAddRecurringPaymentMembership(product, orderItems)) {
      return 0;
    }

    if (!ignoreOnlineSalesClosed && !this.isOnlineSalesOpen(product.id, bookingDate, session)) {
      return 0;
    }

    if (CSessionBasedProductTypes.includes(product.type as TProductType) && !session) {
      return 0;
    }

    if (product.type === 'package') {
      return this.getPackageAmountRemaining(product, orderItems, bookingDate, session);
    }

    const amountRemaining = this.getAmountRemaining(product.id, orderItems, bookingDate, session);

    // If there are no purchase or capacity limits, the remaining amount is the default max minus the current usage
    if (amountRemaining === this.maxOrderItems) {
      const productItemUsage = this.getProductOrderItemUsage(parseInt(product.id), orderItems, bookingDate, session);
      return Math.floor(amountRemaining - productItemUsage);
    }

    return amountRemaining;
  }

  canAddProduct(
    product: IProductInfo,
    orderItems: IOrderItem[],
    bookingDate: Date,
    session?: IProductSession,
    isBookingEdit?: boolean,
    restrictedAddOns?: IRestrictedAddons[]
  ): boolean {
    const maxAmount = this.getMaxAddAmount(product, orderItems, bookingDate, session, isBookingEdit, restrictedAddOns);
    return maxAmount > 0;
  }

  /** Returns the remaining capacity of a given product based on the date, time (session) and rest of the order */
  getRemainingCapacity(
    productId: string,
    orderItems: IOrderItem[],
    bookingDate: Date,
    session?: IProductSession
  ): number {
    const lookup = this.productAvailableHelperService.getCapacityProductAvailabilityLookup(bookingDate);
    const product = lookup?.[productId];
    if (!product) {
      return 0;
    }

    const startTime = session?.startTime ? this.dateFormatService.hourStringToNumber(session.startTime) : undefined;
    const items = this.productAvailableHelperService.mapToCapacityOrderItems(orderItems);

    return this.productCapacityService.getRemainingCapacity(product, lookup, bookingDate, items, startTime);
  }

  /**
   * Check if product has availability for the given number of guests at the given date (and session for session-based products)
   * @returns true if any of the products have availability for the given number of guests, false otherwise
   */
  isProductAvailableForGuests(parentProduct: IProduct, guests: number, date: Date, session?: IProductSession) {
    const isPerGuest = this.productHelperService.isPerGuestProductOrPackage(parentProduct);
    if (!isPerGuest) {
      return parentProduct.products.some((product) => {
        const maxAddAmount = this.getMaxAddAmount(product, [], date, session, false, []);
        const addAmount = Math.ceil(guests / product.groupSize);
        return addAmount <= maxAddAmount;
      });
    }

    return (
      session &&
      parentProduct.products.some((product) => this.isPerGuestSessionAvailableForGuests(product, guests, date, session))
    );
  }

  /**
   * Check if the given date & session of a per-guest duration product has availability for the given number of guests
   * @returns true if the session has availability for the given number of guests, false otherwise
   */
  isPerGuestSessionAvailableForGuests(product: IProductInfo, guests: number, date: Date, session: IProductSession) {
    /**
     * Current compromise for Conqueror: we are assuming that all resources in a game-based product have the same capacity, so we can read the capacity from the first resource.
     * This may not be the case for all game-based products moving forward.
     */
    const maxGuestsPerResource = session.allocations[0].locations[0]?.capacity;
    const maxAddAmount = this.getMaxAddAmount(product, [], date, session, false, []);
    const productId = this.getProductIdForCapacitySession(product);
    const capacitySession = this.productAvailableHelperService.mapToCapacitySession(productId, session);
    return (
      !!maxGuestsPerResource &&
      guests <= maxAddAmount &&
      this.productCapacityService.isSessionAvailableForGuests(capacitySession, guests, maxGuestsPerResource)
    );
  }

  /**
   * Return the available product sessions for a given number of guests
   * @param product - The parent product to filter sessions for
   * @param guests - The number of guests to filter sessions for
   * @param date - The date to filter sessions for
   * @param includeSoldOut - Whether to include sold out sessions
   * @returns The filtered sessions
   */
  filterProductSessionsByGuestCount(
    product: IProduct,
    sessions: IProductSession[],
    date: Date,
    guests: number,
    includeSoldOut?: boolean
  ) {
    if (!sessions.length) {
      return [];
    }

    return sessions
      .map((session) => ({
        ...session,
        isAvailableForGuests: this.isProductAvailableForGuests(product, guests, date, session)
      }))
      .filter((session) => includeSoldOut || session.isAvailableForGuests);
  }

  getOrderItemsByParentProductId(
    parentProductId: number,
    items: IOrderItem[],
    bookingDate: Date,
    session: IProductSession | undefined
  ): IOrderItem[] {
    return items.filter((item: IOrderItem) =>
      this.getMatchingOrderItem(item, bookingDate, session, undefined, parentProductId)
    );
  }

  getOrderItemsByProductId(
    productId: number,
    items: IOrderItem[],
    bookingDate: Date,
    session: IProductSession | undefined
  ): IOrderItem[] {
    return items.filter((item: IOrderItem) => this.getMatchingOrderItem(item, bookingDate, session, productId));
  }

  private getPackageAmountRemaining(
    product: IProductInfo,
    orderItems: IOrderItem[],
    bookingDate: Date,
    session?: IProductSession
  ) {
    // TODO: Logic for calculating amount remaining for option sets
    if (!product.packageItems?.length && product.packageOptionSets?.length) {
      return CMaxOrderItems;
    }

    if (!product.packageItems?.length) {
      return 0;
    }

    const packageItemProduct =
      this.productAvailableHelperService.getCapacityProductAvailabilityLookup(bookingDate)?.[product.id];

    return product.packageItems.reduce((remainingCapacity: number, packageItem) => {
      // Check the package item product amount remaining first, against what's in the cart
      const packageItemRemaining = this.getAmountRemaining(packageItem.productId, orderItems, bookingDate, session);

      // Purchase limits also now apply to the package variation, so check these as well
      const purchaseLimit = this.getRemainingPurchaseLimit(
        packageItemRemaining,
        packageItemProduct,
        orderItems,
        bookingDate,
        session
      );

      // consider the package item quantity
      // i.e. if the package item product has a total amount of 12, the package item has a quantity of 8, only 1 can be added
      const maxPackageItemCapacity = Math.floor(purchaseLimit / packageItem.quantity);

      return Math.min(remainingCapacity, maxPackageItemCapacity);
    }, CMaxOrderItems);
  }

  /**
   * For non-package products, we want us items for calculations to take into account packageOrderItems that could affect the amount remaining.
   *
   * i.e. a packageOrderItem consumes 1 out of 3 spots, if it's matching product is sold on its own, it should have 2 remaining for the given time.
   * This is consistent across purchase limits as well as capacity.
   */
  private getAmountRemaining(
    productId: string,
    orderItems: IOrderItem[],
    bookingDate: Date,
    session?: IProductSession
  ) {
    const product = this.productAvailableHelperService.getCapacityProductAvailabilityLookup(bookingDate)?.[productId];
    const calcItems = this.getItemsForCalculations(orderItems);
    const capacity = this.getRemainingCapacity(productId, calcItems, bookingDate, session);

    return this.getRemainingPurchaseLimit(capacity, product, calcItems, bookingDate, session);
  }

  /**
   * Determine the amount that can be added given the product and parent product limits
   * Taking into account consumption by other matching product/parentProduct order items within the cart
   */
  private getRemainingPurchaseLimit(
    baseAmount: number,
    product: ICXCapacityProduct | undefined,
    orderItems: IOrderItem[],
    bookingDate: Date,
    session?: IProductSession
  ): number {
    if (!product) {
      return 0;
    }

    let amountRemaining = Math.min(baseAmount, this.maxOrderItems);

    if (rdlIsNumber(product.maxPurchase)) {
      const productMaxPurchase = this.getMaxPurchaseRemainingForProduct(product, orderItems, bookingDate, session);
      amountRemaining = Math.min(amountRemaining, productMaxPurchase);
    }

    if (rdlIsNumber(product.parentMaxPurchase)) {
      const parentMaxAdd = this.getMaxPurchaseRemainingForParent(product, orderItems, bookingDate, session);
      amountRemaining = Math.min(amountRemaining, parentMaxAdd);
    }

    amountRemaining = Math.floor(amountRemaining / product.requiredGroupSize);
    const productItemUsage = this.getProductOrderItemUsage(product.productId, orderItems, bookingDate, session);

    // The first add to the cart to adds the minPurchase quantity. We only want to return 0 when the product is not in the cart,
    // and the remaining amount is less than the minPurchase. Otherwise, we could restrict the limit too early i.e. there is
    // min 3 max of 5 and 3 are in the cart meaning amountRemaining is 2 but the min is 3, so we return 0 - we should return 2 in this case.
    if (amountRemaining < product.minPurchase && productItemUsage === 0) {
      return 0;
    }

    return Math.max(amountRemaining, 0);
  }

  private getMaxPurchaseRemainingForProduct(
    product: ICXCapacityProduct,
    orderItems: IOrderItem[],
    bookingDate: Date,
    session?: IProductSession
  ) {
    if (!rdlIsNumber(product.maxPurchase)) {
      return this.maxOrderItems;
    }

    const totalProductUsage = this.getProductOrderItemUsage(product.productId, orderItems, bookingDate, session);

    return product.maxPurchase - totalProductUsage;
  }

  private getMaxPurchaseRemainingForParent(
    product: ICXCapacityProduct,
    orderItems: IOrderItem[],
    bookingDate: Date,
    session?: IProductSession
  ) {
    if (!rdlIsNumber(product.parentMaxPurchase)) {
      return this.maxOrderItems;
    }

    const parentProductId = product.parentProductId;
    const parentOrderItems = this.getOrderItemsByParentProductId(parentProductId, orderItems, bookingDate, session);
    const totalParentUsage = parentOrderItems.reduce(
      (sum, o) => sum + o.quantity * o.groupSize + o.additionalGuests,
      0
    );
    return product.parentMaxPurchase - totalParentUsage;
  }

  private getProductOrderItemUsage(
    productId: number,
    orderItems: IOrderItem[],
    bookingDate: Date,
    session?: IProductSession
  ) {
    const productOrderItems = this.getOrderItemsByProductId(productId, orderItems, bookingDate, session);
    return productOrderItems.reduce((sum, o) => sum + o.quantity * o.groupSize + o.additionalGuests, 0);
  }

  /** You can't add more than one type of recurring payment item in a single order */
  private canAddRecurringPaymentMembership(product: IProductInfo, orderItems: IOrderItem[]) {
    if (product.type !== 'membership' || !product.recurringPayment) {
      return true;
    }

    return orderItems.every(
      (item) =>
        !item.recurringPayment ||
        (item.recurringPayment.numberOfPayments === product.recurringPayment?.numberOfPayments &&
          item.recurringPayment.paymentFrequencyId === product.recurringPayment?.paymentFrequencyId)
    );
  }

  /** Some addons have restrictions based on the day/time they can be added relative to the venue */
  private canAddRestrictedAddon(
    product: IProductInfo,
    session: IProductSession | undefined,
    restrictedAddOns: IRestrictedAddons[] = []
  ): boolean {
    const restriction = restrictedAddOns.find((restricted: IRestrictedAddons) =>
      restricted.addOns.includes(product.parentProductId || '')
    );
    if (product.type !== 'addon' || !restriction?.days || !session) {
      return true;
    }

    const selectedWeekDay = this.dateFormatService.dateOnlyStringToDate(session.date).getDay();
    const selectedSessionTime = this.dateFormatService.hourStringToNumber(session.startTime);
    const stockPeriodDay = restriction.days[selectedWeekDay];
    return (
      !stockPeriodDay.closed &&
      stockPeriodDay.timeRanges.some(
        (range) => selectedSessionTime >= range.startTime && selectedSessionTime < range.endTime
      )
    );
  }

  private getItemsForCalculations(items: IOrderItem[]): IOrderItem[] {
    const flatItems = items.filter((item) => item.productType !== 'package' || !item.packageItems?.length);
    const packageOrderItems = items.filter(
      (item) => item.productType === 'package' && !!item.packageOrderItems?.length
    );

    packageOrderItems.forEach((packageOrderItem) => {
      packageOrderItem.packageOrderItems?.forEach((packageItem) => {
        const packageItemCopy = structuredClone(packageItem);
        packageItemCopy.quantity *= packageOrderItem.quantity;
        packageItemCopy.additionalGuests = packageOrderItem.additionalGuests;
        flatItems.push(packageItemCopy);
      });
    });

    return flatItems;
  }

  /**
   * Ensures that a matching availability and allocation exists for the product by date / session
   * For non-availability products we return true as they are later calculated in max/default quantity
   * For packages, every package item product must meet this methods validation
   */
  public isProductAvailable(productId: string, bookingDate: Date, session?: IProductSession): boolean {
    const product = this.productAvailableHelperService.getCapacityProductAvailabilityLookup(bookingDate)?.[productId];
    if (!product) {
      return false;
    }

    if (product.isPackage) {
      return this.isPackageInclusionsAvailable(product, bookingDate, session);
    }

    if (CSessionBasedProductTypes.includes(product.type)) {
      return !!this.getMatchingProductSession(product.sessions, session)?.isAvailable;
    }

    if (!product.availability && this.typesWithoutRequiredAvailability.includes(product.type)) {
      return true;
    }

    return !!product.availability?.isAvailable;
  }

  private isPackageInclusionsAvailable(product: ICXCapacityProduct, bookingDate: Date, session?: IProductSession) {
    // TODO: Logic for determining package availability based on option sets
    if (!product.packageItems?.length && product.packageOptionSets?.length) {
      return true;
    }

    if (!product.packageItems?.length) {
      return false;
    }

    return product.packageItems.every((packageItem) => {
      return this.isProductAvailable(packageItem.productId.toString(), bookingDate, session);
    });
  }

  /**
   * Ensures that matching availability is marked as open for online sales, for non-availability products this is marked as open
   * For packages, every package item product must meet this methods validation
   */
  private isOnlineSalesOpen(productId: string, bookingDate: Date, session: IProductSession | undefined): boolean {
    const product = this.productAvailableHelperService.getCapacityProductAvailabilityLookup(bookingDate)?.[productId];
    if (!product) {
      return false;
    }

    if (product.isPackage) {
      return this.isPackageInclusionsOnlineSalesOpen(product, bookingDate, session);
    }

    if (CSessionBasedProductTypes.includes(product.type)) {
      return !!this.getMatchingProductSession(product.sessions, session)?.onlineSalesOpen;
    }

    if (!product?.availability && this.typesWithoutRequiredAvailability.includes(product.type)) {
      return true;
    }

    return !!product.availability?.onlineSalesOpen;
  }

  private isPackageInclusionsOnlineSalesOpen(
    product: ICXCapacityProduct,
    bookingDate: Date,
    session: IProductSession | undefined
  ): boolean {
    // Option sets only contain stock currently, so are always treated as online sales open
    if (!product.packageItems?.length && product.packageOptionSets?.length) {
      return true;
    }

    if (!product.packageItems?.length) {
      return false;
    }

    return product.packageItems.every((pi) => {
      return this.isOnlineSalesOpen(pi.productId.toString(), bookingDate, session);
    });
  }

  private getMatchingProductSession(sessions: ICXCapacitySession[] | undefined, session: IProductSession | undefined) {
    if (!session || !sessions?.length) {
      return undefined;
    }

    const dateIndex = this.dateFormatService.toDateIndex(session.date);
    const startTime = this.dateFormatService.hourStringToNumber(session.startTime);

    return sessions.find((s) => s.dateIndex === dateIndex && s.startTime === startTime);
  }

  /** Simple check to determine if an order item matches the given booking date and session - ID's can be checked externally */
  private getMatchingOrderItem(
    item: IOrderItem,
    bookingDate: Date,
    session: IProductSession | undefined,
    productId?: number,
    parentProductId?: number
  ): boolean {
    if ((session || item.session) && !this.productHelperService.isSameSession(item.session, session)) {
      return false;
    }

    if (!this.dateFormatService.isSameDay(item.bookingDate, bookingDate)) {
      return false;
    }

    return parentProductId
      ? parseInt(item.parentProductId) === parentProductId
      : parseInt(item.productId) === productId;
  }

  private getProductIdForCapacitySession(product: IProductInfo) {
    const perGuestPackageItem = this.productHelperService.getPerGuestPackageItem(product);
    return product.type === 'package' && perGuestPackageItem ? perGuestPackageItem.productId : product.id;
  }
}
